import axios from 'axios';
import type { ApiResponse, PagedResponse } from './types';
import { clearAuthTokens } from '@/utils/authStorage';
import { getExternalMemberLoginUrl } from '@/config/externalPortals';

export type { ApiResponse, PagedResponse } from './types';
export type { SearchParams, PaginationParams, SortParams } from './types';

const LOCALE_KEY = 'wems_crm_locale';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'X-WEMS-CSRF': '1',
  },
});

function normalizeApiPath(url: string): string {
  if (/^[a-z][a-z\d+\-.]*:\/\//i.test(url)) return url;
  const normalized = url.replace(/^\/?api\/v1(?=\/|$)/, '');
  return normalized || '/';
}

function clearAuthAndRedirect(): void {
  clearAuthTokens();
  window.location.replace(getExternalMemberLoginUrl());
}

apiClient.interceptors.request.use((config) => {
  delete config.headers.Authorization;
  const locale = localStorage.getItem(LOCALE_KEY) || 'zh-TW';
  config.headers['Accept-Language'] = locale;
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) clearAuthAndRedirect();
    return Promise.reject(error);
  },
);

export async function apiGet<T>(url: string, params?: object): Promise<ApiResponse<T>> {
  const response = await apiClient.get<ApiResponse<T>>(normalizeApiPath(url), { params });
  return response.data;
}

export async function apiPost<T>(url: string, data?: unknown): Promise<ApiResponse<T>> {
  const response = await apiClient.post<ApiResponse<T>>(normalizeApiPath(url), data);
  return response.data;
}

export async function apiPut<T>(url: string, data?: unknown): Promise<ApiResponse<T>> {
  const response = await apiClient.put<ApiResponse<T>>(normalizeApiPath(url), data);
  return response.data;
}

export async function apiDelete<T>(url: string): Promise<ApiResponse<T>> {
  const response = await apiClient.delete<ApiResponse<T>>(normalizeApiPath(url));
  return response.data;
}

export default apiClient;
